import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconCashBanknote, IconPlus } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { useSuppliers } from '../purchasing/purchasingApi';

// Phase Accounting-10: فواتير المشتريات — supplier invoice register +
// payment recording (each payment settles AP in the GL).

interface PurchaseInvoiceDto {
  purchaseInvoiceId: string;
  supplierId: string;
  supplierNameAr: string;
  supplierNameEn: string | null;
  supplierInvoiceRef: string;
  receivedAt: string;
  invoiceDate: string;
  subtotalExTax: number;
  taxTotal: number;
  total: number;
  paidTotal: number;
  status: number;
  notes: string | null;
}

interface PurchaseInvoicePageDto {
  total: number;
  rows: PurchaseInvoiceDto[];
}

const QK = ['purchase-invoices'] as const;

export function PurchaseInvoicesPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [paying, setPaying] = useState<PurchaseInvoiceDto | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [...QK, status, page] as const,
    queryFn: async () =>
      (
        await apiClient.get<PurchaseInvoicePageDto>('/api/accounting/purchase-invoices', {
          params: { status: status ?? undefined, page, pageSize: 50 },
        })
      ).data,
  });

  const statusOptions = [
    { value: '1', label: t('accounting.purchaseInvoices.status.unpaid') },
    { value: '2', label: t('accounting.purchaseInvoices.status.partiallyPaid') },
    { value: '3', label: t('accounting.purchaseInvoices.status.paid') },
    { value: '4', label: t('accounting.purchaseInvoices.status.disputed') },
  ];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.purchaseInvoices.title')}
        subtitle={t('accounting.purchaseInvoices.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between">
          <Select
            data={statusOptions}
            value={status}
            onChange={setStatus}
            placeholder={t('accounting.purchaseInvoices.statusPlaceholder')}
            clearable
            style={{ minWidth: 200 }}
          />
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setCreateOpen(true)}>
            {t('accounting.purchaseInvoices.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.purchaseInvoices.columns.supplier')}</Table.Th>
                <Table.Th>{t('accounting.purchaseInvoices.columns.reference')}</Table.Th>
                <Table.Th>{t('accounting.purchaseInvoices.columns.date')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.purchaseInvoices.columns.total')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.purchaseInvoices.columns.paid')}</Table.Th>
                <Table.Th>{t('accounting.purchaseInvoices.columns.status')}</Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((inv) => (
                <Table.Tr key={inv.purchaseInvoiceId}>
                  <Table.Td>
                    {i18n.language === 'ar'
                      ? inv.supplierNameAr
                      : (inv.supplierNameEn ?? inv.supplierNameAr)}
                  </Table.Td>
                  <Table.Td className="font-numeric">{inv.supplierInvoiceRef}</Table.Td>
                  <Table.Td className="font-numeric">{inv.invoiceDate}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {inv.total.toFixed(2)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {inv.paidTotal.toFixed(2)}
                  </Table.Td>
                  <Table.Td><StatusBadge status={inv.status} /></Table.Td>
                  <Table.Td>
                    {inv.status !== 3 && (
                      <Button
                        size="compact-sm"
                        variant="light"
                        leftSection={<IconCashBanknote size={14} stroke={1.6} />}
                        onClick={() => setPaying(inv)}
                      >
                        {t('accounting.purchaseInvoices.recordPayment')}
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.purchaseInvoices.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data && data.total > 50 && (
            <Group justify="center" p="md">
              <Pagination total={Math.ceil(data.total / 50)} value={page} onChange={setPage} />
            </Group>
          )}
        </Paper>
      )}

      <CreateInvoiceDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: QK })}
      />
      <RecordPaymentModal
        invoice={paying}
        onClose={() => setPaying(null)}
        onPaid={() => qc.invalidateQueries({ queryKey: QK })}
      />
    </Stack>
  );
}

function StatusBadge({ status }: { status: number }) {
  const { t } = useTranslation();
  const map: Record<number, { color: string; key: string }> = {
    1: { color: 'red', key: 'accounting.purchaseInvoices.status.unpaid' },
    2: { color: 'orange', key: 'accounting.purchaseInvoices.status.partiallyPaid' },
    3: { color: 'green', key: 'accounting.purchaseInvoices.status.paid' },
    4: { color: 'gray', key: 'accounting.purchaseInvoices.status.disputed' },
  };
  const m = map[status] ?? map[1]!;
  return <Badge size="xs" variant="light" color={m!.color} radius="sm">{t(m!.key)}</Badge>;
}

function CreateInvoiceDrawer({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const suppliers = useSuppliers();
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(new Date());
  const [subtotal, setSubtotal] = useState<number>(0);
  const [tax, setTax] = useState<number>(0);
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/accounting/purchase-invoices', {
          supplierId,
          supplierInvoiceRef: reference.trim(),
          invoiceDate: toIsoDate(invoiceDate!),
          purchaseOrderId: null,
          subtotalExTax: subtotal,
          taxTotal: tax,
          notes: notes.trim() || null,
        })
      ).data,
  });

  const supplierOptions = useMemo(
    () =>
      (suppliers.data ?? []).map((s) => ({
        value: s.supplierId,
        label: i18n.language === 'ar' ? s.nameAr : (s.nameEn ?? s.nameAr),
      })),
    [suppliers.data, i18n.language],
  );

  const valid = supplierId !== null && reference.trim() !== '' && invoiceDate !== null
    && subtotal + tax > 0;

  const submit = async () => {
    try {
      await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: reference });
      onCreated();
      onClose();
      setSupplierId(null); setReference(''); setSubtotal(0); setTax(0); setNotes('');
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('accounting.purchaseInvoices.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="md"
      padding="lg"
    >
      <Stack gap="md">
        <Select
          label={t('accounting.purchaseInvoices.columns.supplier')}
          data={supplierOptions}
          value={supplierId}
          onChange={setSupplierId}
          searchable
          withAsterisk
        />
        <TextInput
          label={t('accounting.purchaseInvoices.form.referenceLabel')}
          description={t('accounting.purchaseInvoices.form.referenceHelp')}
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
          withAsterisk
        />
        <DatePickerInput
          label={t('accounting.purchaseInvoices.form.dateLabel')}
          value={invoiceDate}
          onChange={setInvoiceDate}
        />
        <Group grow>
          <NumberInput
            label={t('accounting.purchaseInvoices.form.subtotalLabel')}
            value={subtotal}
            onChange={(v) => setSubtotal(Number(v) || 0)}
            min={0}
            decimalScale={2}
            hideControls
          />
          <NumberInput
            label={t('accounting.purchaseInvoices.form.taxLabel')}
            value={tax}
            onChange={(v) => setTax(Number(v) || 0)}
            min={0}
            decimalScale={2}
            hideControls
          />
        </Group>
        <Text size="sm" c="dimmed" className="font-numeric">
          {t('accounting.purchaseInvoices.columns.total')}: {(subtotal + tax).toFixed(2)}
        </Text>
        <Textarea
          label={t('accounting.purchaseInvoices.form.notesLabel')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          rows={2}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function RecordPaymentModal({
  invoice, onClose, onPaid,
}: { invoice: PurchaseInvoiceDto | null; onClose: () => void; onPaid: () => void }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<number>(0);
  const remaining = invoice ? invoice.total - invoice.paidTotal : 0;

  const pay = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/accounting/purchase-invoices/${invoice!.purchaseInvoiceId}/payments`,
          { amount },
        )
      ).data,
  });

  const submit = async () => {
    try {
      await pay.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.updated'), message: '' });
      onPaid();
      onClose();
      setAmount(0);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Modal
      opened={invoice !== null}
      onClose={onClose}
      title={t('accounting.purchaseInvoices.recordPayment')}
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed" className="font-numeric">
          {t('accounting.purchaseInvoices.form.remaining')}: {remaining.toFixed(2)}
        </Text>
        <NumberInput
          label={t('accounting.purchaseInvoices.form.amountLabel')}
          value={amount}
          onChange={(v) => setAmount(Number(v) || 0)}
          min={0}
          max={remaining}
          decimalScale={2}
          hideControls
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={pay.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={amount <= 0 || amount > remaining}
            loading={pay.isPending}
          >
            {t('accounting.purchaseInvoices.recordPayment')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
